import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';
import * as cronExpressionValidator from 'cron-expression-validator';

import { ConnectionPointType, Miniature, ComponentDefinition, ContextualField, PropertyType, IApprovalStrategy, MailingJobDefinitionDto, KeyValuePair } from '../../../models';
import { MailingJobDefinition } from '../../../models/mailing/MailingJobDefinition';
import { ILoggerService, IMailingJobService, ICurrentApplicationService, IApprovalStrategyService, IStoreService } from '../../../services';
import { guidEmpty } from '../../../common';
import * as Events from '../../../common/events';
import * as Fields from '../../../common/helpers/contextual';
import { MailingJobCondition } from '../../../models/mailing/MailingJobCondition';
import { remapMailRecipients } from '../../../models/mailing/remapMailRecipients';


class MailingJobMiniature extends Miniature {
    constructor() {
        super('mailingJob', 'Scheduled email', 'fa-envelope-o', ['A new scheduled email']);
    }

}
class EventEmailMiniature extends Miniature {
    constructor() {
        super('eventEmail', 'Event based email', 'fa-envelope', ['A new event based email']);
    }
}

class MailingJobContextualController implements angular.IComponentController {
    selectedItem: MailingJobDefinition;
    types = [ConnectionPointType.SMTP];
    formController: angular.IFormController;
    miniatures: Miniature[];
    externalFormController: angular.IFormController;
    redirect: boolean;
    properties: ContextualField[];
    approvalStrategyLevels: KeyValuePair<number, string>[];
    previousStrategy: string;

    static $inject = [
        '$rootScope',
        '$scope',
        '$state',
        '$timeout',
        'storeService',
        'mailingJobService',
        'loggerService',
        'currentApplicationService',
        'approvalStrategyService'
    ];
    
    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$scope: angular.IScope,
        private _$state: angular.ui.IStateService,
        private _$timeout: angular.ITimeoutService,
        private _storeService: IStoreService,
        private _mailingJobService: IMailingJobService,
        private _loggerService: ILoggerService,
        private _currentApplicationService: ICurrentApplicationService,
        private _approvalStrategyService: IApprovalStrategyService,
    ) {
    }

    $onInit() {
        this.miniatures = [
            new MailingJobMiniature(),
            new EventEmailMiniature()
        ];

        const connectionPointGuid = Object.assign({}, Fields.ConnectionPointGuid);
        connectionPointGuid.extraData = {
            types: this.types
        };

        this.properties = [
            {
                modelName: 'messageTitle',
                propertyType: PropertyType.Text,
                required: true,
                icon: 'fa-header',
                label: 'Subject',
                propertyName: 'subjectProperty',
                messages: [{
                    key: 'required', value: 'A subject is required'
                }]
            },
            {
                modelName: 'isFrequencyManual',
                propertyType: PropertyType.Boolean,
                icon: 'fa-hand-o-down',
                label: 'Manual setting',
                propertyName: 'isFrequencyManualProperty',
                onChange: () => this._$timeout(() => this.computeVisibility())
            },
            {
                modelName: 'frequency',
                propertyType: PropertyType.Text,
                required: true,
                icon: 'fa-clock-o',
                label: 'Frequency',
                propertyName: 'frequencyProperty',
                messages: [
                    { key: 'required', value: 'A frequency is required' },
                    { key: 'invalid', value: 'A valid cron expression is required' }
                ],
                onChange: () => this._$timeout(() => this.validateCron())
            },
            connectionPointGuid,
            angular.copy(Fields.IsSingleDashboardFile),
            this.getIsExportToChildApplication(),
            {
                modelName: 'condition',
                propertyType: PropertyType.MailingJobCondition,
                required: false,
                iconText: '◊',
                label: 'Condition',
                propertyName: 'conditionProperty',
            },
            this.getApprovalStrategyField(),
            this.getApprovalLevelsField()
        ];

        this._$scope.$watch(
            () => this.selectedItem,
            () => this.onUpdateSelected()
        );

        this._$scope.$watch(
            () => this.selectedItem?.condition,
            () => this.computeVisibility()
        );

        this.computeVisibility();
    }

    save() {
        this.selectedItem.startDate = moment(this.selectedItem.startDate).toISOString();
        this.selectedItem.dashboards = this.selectedItem.dashboards
            ? this.selectedItem.dashboards.filter(d => d.guid && d.guid !== guidEmpty)
            : [];

        // for compat : prevent duplicate
        remapMailRecipients(this.selectedItem, this.selectedItem['selectedChoices']);
      
        let promise: angular.IPromise<MailingJobDefinitionDto>;
        if (!this.selectedItem.guid || this.selectedItem.guid === guidEmpty) {
            promise = this._mailingJobService.add(this.selectedItem);
        } else {
            promise = this._mailingJobService.update(this.selectedItem);
        }
        return promise
            .then(data => {
                this.selectedItem.guid = data.guid;
                this._loggerService.logSuccess('Successfully saved mailing job!');
                this.clean();
                this._storeService.dispatchers().dashboardUsers.getAll(true);
            })
            .catch(() => {
                this._loggerService.logError('Failed to save mailing job.');
            });
    }

    cancel() {
        this.clean();
    }

    isSaveEnabled() {
        let isValid = this.formController.$valid && (this.formController.$dirty);
        if (this.externalFormController) {
            isValid = isValid || (this.externalFormController.$dirty && this.externalFormController.$valid);
        }
        return isValid;
    }

    changeDate() {
        this.formController.$setDirty();
    }

    private validateCron() {
        const isValid = cronExpressionValidator.isValidCronExpression(this.selectedItem?.frequency);
        this.formController.frequencyProperty.$setValidity('invalid', isValid);
    }

    private onUpdateSelected() {
        if (this.selectedItem && this.selectedItem.approvalStrategyGuid) {
            if (this.previousStrategy === undefined) {
                this.previousStrategy = this.selectedItem.approvalStrategyGuid;
            }
            this.changeStrategy(this.selectedItem.approvalStrategyGuid);
        }
        this.computeVisibility();
    }

    private computeVisibility() {
        const indexStrategy = _.findIndex(this.properties, p => p.propertyType === PropertyType.ApprovalStrategyGuid);
        const indexLevels = _.findIndex(this.properties, p => p.propertyType === PropertyType.ApprovalMultiLevel);
        const hideApproval = this.selectedItem?.condition !== MailingJobCondition.NotYetApproved;
        this.properties[indexStrategy].hide = hideApproval;
        this.properties[indexLevels].hide = hideApproval;

        const indexFrequency = _.findIndex(this.properties, p => p.propertyName === 'frequencyProperty');
        this.properties[indexFrequency].disabled = !this.selectedItem?.isFrequencyManual;
    }

    private changeStrategy(guid: string) {
        this._approvalStrategyService.getAll().then(all => {
            const strategy = _.find<IApprovalStrategy>(all, s => s.guid === guid);
            if (strategy) {
                if (this.previousStrategy !== guid) {
                    this.selectedItem.approvalLevels = [];
                }
                this.approvalStrategyLevels = strategy
                    .approbationStrategyLevelCollection
                    .map(l => {
                        return { key: l.level, value: `L${(l.level + 1)}` };
                    });

                const index = _.findIndex(this.properties, p => p.propertyType === PropertyType.ApprovalMultiLevel);
                this.properties[index].extraData.options = this.approvalStrategyLevels;
            }
        });
    }

    private clean() {
        this._$rootScope.$broadcast(Events.mailingJobUpdated);
        this.formController.$setPristine();
        if (this.externalFormController) {
            this.externalFormController.$setPristine();
        }
        if (this.redirect) {
            this._$state.go('emails');
        }
        this.selectedItem = null;
    }

    private getIsExportToChildApplication(): ContextualField {
        const isChildAllowed = this._currentApplicationService.getApplicationConfiguration().isChildApplicationCreationAllowed;
        const result = angular.copy(Fields.IsExportToChildApplication);
        result.hide = !isChildAllowed;
        return result;
    }

    private getApprovalStrategyField(): ContextualField {
        const result = angular.copy(Fields.ApprovalStrategyGuid);
        result.onChange = (guid) => this.changeStrategy(guid);
        return result;
    }

    private getApprovalLevelsField(): ContextualField {
        const result = angular.copy(Fields.ApprovalLevels);
        result.extraData = {
            options: this.approvalStrategyLevels
        };
        return result;
    }
}

export const wtMailingJobContextual: ComponentDefinition = {
    componentId: 'wtMailingJobContextual',
    controller: MailingJobContextualController,
    template: require('./mailingJobContextual.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedItem: '=',
        externalFormController: '<?',
        redirect: '<'
    }
};